import React, { useState, useImperativeHandle, useCallback, useMemo } from 'react';
import { FlatList, Image, ScrollView, Text, TouchableOpacity, View } from 'react-native-web';
import PropTypes from 'prop-types';
import * as Animatable from 'react-native-animatable';
import FeatherIcon from 'react-native-vector-icons/dist/Feather';
import AwesomeIcon from 'react-native-vector-icons/dist/FontAwesome';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

// Custom Components
import { InScreenModal, NavBar, TextBox } from '../../components';

// Assets
import { ReactComponent as StripeLogo } from '../../images/image_stripe_color.svg';

// Utils
import { showAlert, showAppError, toTitleCase } from '../../utils/util_helpers';
import { getCardImage, getCardNumberFormat, getCardValidity, showStripeInfo } from '../../utils/util_credit_card';

// APIs
import { deletePaymentMethod, getNewPaymentMethodIntentData, getPaymentMethods, setDefaultPaymentMethod } from '../../modules/api_tiers';

// Configs
import GLOBAL from '../../configs/config_global';
import LoadingView from '../../components/component_loading_view';
import { getGlobalState, setGlobalState, STATE_KEYS } from '../../configs/config_state';
import { StripePaymentForm } from '../../components/component_stripe_modal';
import { cloneDeep } from 'lodash';
import * as RootNavigation from '../../navigation/RootNavigation';
import routeKeys from '../../navigation/RouteKeys';

// Animated Components
const AnimatableFlatList = Animatable.createAnimatableComponent(FlatList);

const SavedPayment = React.forwardRef((props, ref) => {
    // region Props
    const {
        onCardAdded = () => {},
        onCardRemoved = () => {},
        isSelectingAfterTrialTier = false,
        onCardSelectedForTier = () => {}
    } = props;
    // endregion

    // region Stripe Key
    const stripeLoader = useMemo(() => loadStripe(getGlobalState(STATE_KEYS.IS_DEV) ? process.env.REACT_APP_KEY_STRIPE_PUBLISHABLE_SANDBOX : process.env.REACT_APP_KEY_STRIPE_PUBLISHABLE_PRODUCTION), []);
    // endregion

    // region Local State
    const [visible, setVisible] = useState(false);
    const [isPendingRestart, setPendingRestart] = useState(false);
    const [isLoadingPaymentFlow, setLoadingPaymentFlow] = useState(false);
    const [isLoadingCards, setLoadingCards] = useState(false);
    const [cardList, setCardList] = useState([]);
    const [isRemovingCard, setRemovingCard] = useState(false);
    const [isSavingPrimary, setSavingPrimary] = useState(false);
    const [selectedTier, setSelectedTier] = useState(0);
    const [isReplacingFailedPayment, setReplacingFailedPayment] = useState(false);

    const [stripeClientSecret, setStripeClientSecret] = useState('');
    const [showStripeModal, setShowStripeModal] = useState(false);
    const [isSavingPayment, setSavingPayment] = useState(false);
    // endregion

    // region Function - Close Stripe Modal
    const closeStripeModal = useCallback(() => {
        setStripeClientSecret('');
        setShowStripeModal(false);
        setLoadingPaymentFlow(false);
        setSavingPayment(false);
    }, []);
    // endregion

    // region Function - Reset Local State
    const resetLocalState = useCallback(() => {
        setVisible(false);
        setPendingRestart(false);
        setLoadingCards(false);
        setCardList([]);
        setRemovingCard(false);
        setSelectedTier(0);
        setReplacingFailedPayment(false);

        // Stripe Modal States
        closeStripeModal()
    }, [closeStripeModal]);
    // endregion

    // region Function - Get List of Saved Cards
    const goFetchCardList = useCallback(() => {
        setLoadingCards(true);
        getPaymentMethods()
            .then((result) => setCardList(result))
            .catch((error) => showAppError(error))
            .finally(() => setLoadingCards(false));
    }, []);
    // endregion

    // region Function - Fetch Intent for New Payment Method
    const goFetchClientSecret = useCallback(() => {
        setLoadingPaymentFlow(true);
        setTimeout(() => {
            getNewPaymentMethodIntentData(isSelectingAfterTrialTier ? selectedTier : null)
                .then((result) => {
                    setStripeClientSecret(result['client_secret']);
                    setShowStripeModal(true);
                })
                .catch((error) => showAppError(error))
                .finally(() => setLoadingPaymentFlow(false));
        }, 1000);
    }, [isSelectingAfterTrialTier, selectedTier]);
    // endregion

    // region Function - Remove Card
    const goRemoveCard = (cardID, cardNumber, isDefault = false) => {
        if (isDefault) {
            showAlert({ text: 'This payment method is currently set as your primary, please select another primary payment method before removing this one' });
            return;
        }

        showAlert({
            text: 'Are you sure you want to remove this saved payment method?<br/><br/>' + cardNumber,
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                color: 'red',
                onPress: () => {
                    setRemovingCard(true);
                    deletePaymentMethod(cardID)
                        .then(() => {
                            // Update number of cards in global
                            GLOBAL.DATA.USER['total_stripe_cards'] -= 1;
                            onCardRemoved();

                            // Update local state
                            setCardList(cardList.filter((item) => item.id !== cardID));

                            // Refresh Profile Tab
                            setGlobalState(STATE_KEYS.PROFILE_REFRESH_TRIGGERED, true);
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => setRemovingCard(false));
                }
            }
        })
    }
    // endregion

    // region Function - Set Default Payment Method
    const goSetDefaultPayment = (cardData) => {
        if (cardData['is_default']) {
            showAlert({ text: 'This payment method is currently set as your primary, and it will be used for charges with subscription tier' });
            return;
        }

        showAlert({
            text: 'Confirm setting this payment method as primary?<br/><br/>' + getCardNumberFormat(cardData['card_brand'], cardData['last_4']),
            cancelButton: { text: 'Cancel' },
            confirmButton: {
                text: 'Confirm',
                onPress: () => {
                    setSavingPrimary(true);
                    setDefaultPaymentMethod(cardData.id)
                        .then(() => {
                            const tempList = cloneDeep(cardList);
                            const oldPrimaryIndex = tempList.findIndex((item) => item['is_default'] === 1);
                            const cardIndex = tempList.findIndex((item) => item.id === cardData.id);
                            if (oldPrimaryIndex > -1) {
                                tempList[oldPrimaryIndex]['is_default'] = 0;
                            }
                            if (cardIndex > -1) {
                                tempList[cardIndex]['is_default'] = 1;
                            }
                            setCardList(tempList);
                        })
                        .catch((error) => showAppError(error))
                        .finally(() => setSavingPrimary(false));
                }
            }
        })
    }
    // endregion

    // region Function - On Expired Card Pressed
    const _onExpiredCardPressed = (item) => {
        showAlert({
            text: `This card has already expired in ${item['exp_month'] + '/' + item['exp_year']}, do you want to remove it?\n\nAfter removing this card, you can add a new one by tapping on the plus button on the top right corner.`,
            cancelButton: { text: 'No' },
            confirmButton: {
                text: 'Yes',
                onPress: () => goRemoveCard(item.id, getCardNumberFormat(item['card_brand'], item['last_4']), item['is_default'])
            }
        });
    };
    // endregion

    // region Function - Show Modal
    const showModal = useCallback((preSelectedTier = 0, replacingPayment = false) => {
        setVisible(true);
        setLoadingCards(true);
        setSelectedTier(preSelectedTier);
        setReplacingFailedPayment(replacingPayment);
        setTimeout(() => goFetchCardList(), 1000);
        if (replacingPayment) {
            goFetchClientSecret();
        }
    }, [goFetchCardList, goFetchClientSecret]);
    // endregion

    // region Expose Methods
    useImperativeHandle(ref, () => ({ showModal, hideModal: resetLocalState }), [showModal, resetLocalState]);
    // endregion

    // region Render Empty View
    const renderEmptyView = () => (
        <Animatable.View
            animation={'fadeIn'}
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 40, paddingHorizontal: 56 }}>
            <Text onPress={() => goFetchClientSecret()} style={{ color: GLOBAL.COLOR.TEXT_GRAY, textAlign: 'center', fontSize: 16, lineHeight: 28 }}>
                You do not have any card saved with this truck,{'\n'}click this message to add a new card.
            </Text>

            {/* Square Information */}
            <View style={{ alignItems: 'center', alignSelf: 'stretch', marginVertical: 32 }}>
                <TouchableOpacity onPress={() => showStripeInfo()}>
                    <StripeLogo height={48} width={48 * (4000 / 908)} />
                </TouchableOpacity>
            </View>

            {/* Refresh Message */}
            <Text
                onPress={() => goFetchCardList()}
                style={{ color: GLOBAL.COLOR.BASIC.UI_BLUE, textAlign: 'center', fontSize: 16, lineHeight: 28 }}>
                If you recently added a new card, it may take a few minutes to appear here.
                {'\n'}Click this message to refresh the list.
            </Text>
        </Animatable.View>
    )
    // endregion

    // region Render Saved Card Item
    const renderSavedCardItem = (item) => {
        /*
            id: 14
            is_default: 0
            card_brand: 'visa'
            exp_month: 3
            exp_year: 2042
            last_4: '0005'
        */
        const cardValidity = getCardValidity(item['exp_month'], item['exp_year']);
        console.log('card image', getCardImage(item['card_brand']));
        return(
            <View
                key={`cardItem_${item.id}`}
                style={{ flexDirection: 'row', alignItems: 'center', marginTop: 16, width: '100%' }}>
                {/* Card Info */}
                <TextBox
                    style={{ flex: 1, marginTop: 0 }}
                    title={toTitleCase(item['card_brand'])}
                    contentBoxStyle={{
                        backgroundColor: item['is_default'] ? GLOBAL.COLOR.BASIC.GOLD : 'rgb(252, 252, 252)',
                        borderWidth: 1, borderRadius: 6, borderColor: GLOBAL.COLOR.BASIC.NEUTRAL_STROKE,
                        paddingHorizontal: 16, flexDirection: 'row', alignSelf: 'stretch', alignItems: 'center', justifyContent: 'center'
                    }}
                    showHintButton={cardValidity !== 0}
                    hintButtonColor={cardValidity === 1 ? GLOBAL.COLOR.BASIC.GOLD : GLOBAL.COLOR.BASIC.RED}
                    hintButtonImage={cardValidity === 1 ? 'warning' : 'error'}
                    hintButtonOnPress={() => {
                        if (cardValidity === 1) {
                            showAlert({ text: `This card is about to expire in ${item['exp_month'] + '/' + item['exp_year']}, please consider renewing it.` });
                        }
                        else _onExpiredCardPressed(item);
                    }}
                    customImage={getCardImage(item['card_brand'])}
                    customImageNoTint={true}
                    editable={false}
                    textInputStyle={[{ letterSpacing: 4, fontSize: 18 }, cardValidity === 2 && { color: GLOBAL.COLOR.BASIC.RED }]}
                    value={getCardNumberFormat(item['card_brand'], item['last_4'])}
                    useTextView={true}
                    onPress={(() => cardValidity === 2 ? _onExpiredCardPressed(item) : (isSelectingAfterTrialTier ? goFetchClientSecret() : goSetDefaultPayment(item)))}
                />

                {/* Remove Button */}
                <TouchableOpacity
                    onPress={() => goRemoveCard(item.id, getCardNumberFormat(item['card_brand'], item['last_4']), item['is_default'])}
                    style={{ alignItems: 'flex-end', justifyContent: 'center', marginLeft: 16 }}>
                    <FeatherIcon name={'trash-2'} size={28} color={GLOBAL.COLOR.BASIC.GRAY_OPAQUE} />
                </TouchableOpacity>
            </View>
        );
    }
    // endregion

    // region Render Saved Card List
    const renderSavedCardList = () => {
        const cardCount = cardList.length;
        return(
            <View style={{ flex: 1, width: '100%' }}>
                {/* Title Bar */}
                <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%', height: 48, paddingHorizontal: 20, paddingTop: 16 }}>
                    {/* Title */}
                    <Text style={{ color: 'black', fontWeight: 'bold', flex: 1 }}>
                        Saved Payment Method {cardCount > 0 ? '(' + cardCount + ')' : ''}
                    </Text>

                    {/* Add Card Button */}
                    <TouchableOpacity
                        onPress={() => goFetchClientSecret()}>
                        <FeatherIcon name={'plus-circle'} size={28} color={GLOBAL.COLOR.BASIC.GREEN} />
                    </TouchableOpacity>
                </View>

                {/* Saved Card List */}
                <View style={{ width: '100%', flex: 1 }}>
                    <AnimatableFlatList
                        animation={'fadeIn'}
                        style={{ flex: 1, width: '100%' }}
                        contentContainerStyle={{ paddingHorizontal: 20 }}
                        data={cardList}
                        renderItem={({ item }) => renderSavedCardItem(item)}
                        keyExtractor={(item) => item.id?.toString()}
                        showsVerticalScrollIndicator={false}
                    />

                    {/* Gradient Down */}
                    <Image
                        source={require('../../images/image_gradient_down.png')}
                        style={{ position: 'absolute', left: 0, top: 0, right: 16, height: 20 }}
                    />

                    {/* Gradient */}
                    <Image
                        source={require('../../images/image_gradient.png')}
                        style={{ position: 'absolute', left: 0, bottom: 0, right: 16, height: 20 }}
                    />
                </View>
            </View>
        );
    }
    // endregion

    // region Render Stripe Modal
    const renderStripeModal = () => (
        <InScreenModal visible={showStripeModal} style={{ marginVertical: '5%' }}>
            {/* Nav Bar */}
            <NavBar
                title={'Add New Payment Method'}
                subTitle={'Powered by Stripe'}
                hideBorder={false}
                leftButtonOnPress={() => setShowStripeModal(false)}
                rightButtonIconName={'info'}
                rightButtonColor={GLOBAL.COLOR.BASIC.UI_BLUE}
                rightButtonOnPress={() => showStripeInfo()}
            />

            {/* Stripe Payment Form */}
            <ScrollView style={{ flex: 1, width: '100%' }} contentContainerStyle={{ padding: 24 }}>
                {
                    !!stripeClientSecret?.trim() &&
                        <Elements stripe={stripeLoader} options={{ clientSecret: stripeClientSecret }}>
                            <StripePaymentForm
                                stripeClientSecret={stripeClientSecret}
                                onStripeLoading={setSavingPayment}
                                onResult={(result) => {
                                    console.log('result', result);
                                    if (result.error) {
                                        if (result.error.code !== 'Canceled') showAppError(`${result.error.message}<br/><br/>[Error Code: ${result.error.code}]`);
                                    }
                                    else {
                                        console.log('here 1');
                                        setTimeout(() => {
                                            console.log('here 2');
                                            // If this is Free Trial selecting subscription tier
                                            isSelectingAfterTrialTier && onCardSelectedForTier(selectedTier);

                                            // Update the total number or cards in global
                                            GLOBAL.DATA.USER['total_stripe_cards'] += 1;
                                            onCardAdded();

                                            // Reset local state and close modal
                                            if (isReplacingFailedPayment) {
                                                console.log('here 3');
                                                showAlert({
                                                    text: 'Your primary payment method has been updated and will be used for future charges, please restart now to regain access to your subscription tier.',
                                                    cancelButton: {
                                                        text: 'OK',
                                                        onPress: () => RootNavigation.reset(routeKeys.BLANK)
                                                    }
                                                })
                                            }
                                            else {
                                                console.log('here 4');
                                                closeStripeModal();
                                                goFetchCardList();

                                                // Close Saved Payment Modal if selecting trial tier
                                                if (isSelectingAfterTrialTier) {
                                                    resetLocalState();
                                                }
                                                else showAlert({ text: 'Your payment method has been added successfully and set as primary' });
                                            }
                                        }, 1000);
                                    }
                                }}
                            />
                        </Elements>
                }
            </ScrollView>

            {/* Loading View */}
            {
                isSavingPayment &&
                    <LoadingView isLoading={true} description={'Saving payment method'} isAbsolute={true} />
            }
        </InScreenModal>
    )
    // endregion

    // region MAIN RENDER
    return(
        <InScreenModal visible={visible}>
            {/* Nav Bar */}
            <NavBar
                title={'Saved Payment Methods'}
                subTitle={'Powered by Stripe'}
                hideBorder={false}
                leftButtonOnPress={() => resetLocalState()}
                rightButtonIconName={'info'}
                rightButtonColor={GLOBAL.COLOR.BASIC.UI_BLUE}
                rightButtonOnPress={() => showStripeInfo()}
            />

            {/* Card Selection for After Free Trial */}
            {
                isSelectingAfterTrialTier &&
                <View style={{ width: '100%', paddingHorizontal: 16, paddingVertical: 16, backgroundColor: '#FFA600', flexDirection: 'row', alignItems: 'center' }}>
                    <AwesomeIcon name={'info-circle'} size={24} color={GLOBAL.COLOR.BASIC.BLACK_TWO} />
                    <Text style={{ fontSize: 16, fontWeight: '450', color: GLOBAL.COLOR.BASIC.BLACK_TWO, marginHorizontal: 16, lineHeight: 26, flex: 1 }}>
                        Select or add a payment method to be used for your subscription tier after the free trial period
                    </Text>
                </View>
            }

            {/* Content */}
            {
                isLoadingCards ?
                    <LoadingView isLoading={true} description={'Loading saved payment methods'} />
                    :
                    <View style={{ flex: 1, width: '100%' }}>
                        {/* Main View */}
                        {cardList.length ? renderSavedCardList() : renderEmptyView()}
                    </View>
            }

            {/* Stripe Modal */}
            {renderStripeModal()}

            {/* Stripe Loading View */}
            {
                isLoadingPaymentFlow &&
                <LoadingView
                    isLoading={true}
                    description={'Loading Stripe Payment'}
                    isAbsolute={true}
                    style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
                    customTextStyle={{ color: 'white' }}
                />
            }

            {/* Other Loading Views */}
            {
                (isRemovingCard || isSavingPrimary) &&
                <LoadingView isLoading={true} description={isSavingPrimary ? 'Setting default payment method' : 'Removing saved payment method'} isAbsolute={true} />
            }
        </InScreenModal>
    );
    // endregion
});

SavedPayment.propTypes = {
    onCardAdded: PropTypes.func,
    onCardRemoved: PropTypes.func,
    isSelectingAfterTrialTier: PropTypes.bool,
    onCardSelectedForTier: PropTypes.func
};

export default SavedPayment;
